import { useState, type FormEvent } from "react";
import { api, callableErrorMessage } from "../lib/api";

type Props = {
  tenantId: string;
  initialIdentifiers: string[];
  initialTaxIds: string[];
  initialProducts: string[];
  initialPrices: number[];
};

/**
 * Editor manual de palabras clave OCR. Permite al tenant_admin agregar/quitar
 * sin necesidad de subir una boleta. Complemento del OcrTrainer (que detecta automático).
 */
export function OcrManualEditor({
  tenantId,
  initialIdentifiers,
  initialTaxIds,
  initialProducts,
  initialPrices
}: Props) {
  const [identifiers, setIdentifiers] = useState<string[]>(initialIdentifiers);
  const [taxIds, setTaxIds] = useState<string[]>(initialTaxIds);
  const [products, setProducts] = useState<string[]>(initialProducts);
  const [prices, setPrices] = useState<number[]>(initialPrices);

  const [newIdentifier, setNewIdentifier] = useState("");
  const [newTaxId, setNewTaxId] = useState("");
  const [newProduct, setNewProduct] = useState("");
  const [newPrice, setNewPrice] = useState("");

  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const addItem = <T,>(list: T[], setter: (l: T[]) => void, item: T): void => {
    if (!list.includes(item)) setter([...list, item]);
  };
  const removeItem = <T,>(list: T[], setter: (l: T[]) => void, item: T): void => {
    setter(list.filter((x) => x !== item));
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFeedback(null);
    try {
      await api.updateTenantOcrConfig({
        tenantId,
        config: {
          identifiers,
          taxIdHints: taxIds,
          knownProducts: products,
          validPrices: prices
        }
      });
      setFeedback("Guardado");
      setTimeout(() => setFeedback(null), 3000);
    } catch (err) {
      setFeedback(`Error: ${callableErrorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="flex flex-col gap-4">
      <Field
        title="RUT del negocio"
        hint="Acepta con o sin puntos. Ej: 77.411.698-2 o 77411698-2."
        items={taxIds}
        formatter={(s) => s as string}
        onAdd={() => {
          const v = newTaxId.trim();
          if (v) {
            addItem(taxIds, setTaxIds, v);
            setNewTaxId("");
          }
        }}
        onRemove={(v) => removeItem(taxIds, setTaxIds, v as string)}
        inputValue={newTaxId}
        onChange={setNewTaxId}
        placeholder="77.411.698-2"
      />

      <Field
        title="Identificadores (razon social, direccion, fantasy name)"
        hint="Cualquier palabra clave que aparece en tu boleta. Min 1 obligatorio."
        items={identifiers}
        formatter={(s) => s as string}
        onAdd={() => {
          const v = newIdentifier.trim().toLowerCase();
          if (v) {
            addItem(identifiers, setIdentifiers, v);
            setNewIdentifier("");
          }
        }}
        onRemove={(v) => removeItem(identifiers, setIdentifiers, v as string)}
        inputValue={newIdentifier}
        onChange={setNewIdentifier}
        placeholder="fresherb · triana 851 · ..."
      />

      <Field
        title="Productos"
        hint="Para detectar item especifico del reward (opcional)."
        items={products}
        formatter={(s) => s as string}
        onAdd={() => {
          const v = newProduct.trim().toLowerCase();
          if (v) {
            addItem(products, setProducts, v);
            setNewProduct("");
          }
        }}
        onRemove={(v) => removeItem(products, setProducts, v as string)}
        inputValue={newProduct}
        onChange={setNewProduct}
        placeholder="matcha latte"
      />

      <Field
        title="Precios validos"
        hint="Precios tipicos (rangos, productos, etc). Opcional."
        items={prices}
        formatter={(n) => `$${(n as number).toLocaleString("es-CL")}`}
        onAdd={() => {
          const v = Number(newPrice);
          if (Number.isFinite(v) && v > 0) {
            addItem(prices, setPrices, v);
            setNewPrice("");
          }
        }}
        onRemove={(v) => removeItem(prices, setPrices, v as number)}
        inputValue={newPrice}
        onChange={setNewPrice}
        placeholder="3990"
        inputType="number"
      />

      {feedback && (
        <p
          className={`rounded-xl px-3 py-2 text-xs ${
            feedback === "Guardado"
              ? "bg-leaf-50 text-leaf-800"
              : "bg-red-50 text-red-700"
          }`}
        >
          {feedback === "Guardado" ? "✓ Guardado" : feedback}
        </p>
      )}

      <button type="submit" disabled={busy} className="btn-primary">
        {busy ? "Guardando..." : "Guardar ajustes de lectura"}
      </button>
    </form>
  );
}

type FieldProps<T> = {
  title: string;
  hint: string;
  items: T[];
  formatter: (v: T) => string;
  onAdd: () => void;
  onRemove: (v: T) => void;
  inputValue: string;
  onChange: (s: string) => void;
  placeholder: string;
  inputType?: string;
};

function Field<T>({
  title,
  hint,
  items,
  formatter,
  onAdd,
  onRemove,
  inputValue,
  onChange,
  placeholder,
  inputType
}: FieldProps<T>) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="text-xs uppercase tracking-wider text-leaf-600">{title}</p>
        <p className="text-[11px] text-leaf-500">{hint}</p>
      </div>
      <div className="flex gap-2">
        <input
          type={inputType ?? "text"}
          value={inputValue}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAdd();
            }
          }}
          placeholder={placeholder}
          className="input flex-1 text-sm"
        />
        <button
          type="button"
          onClick={onAdd}
          className="rounded-full bg-leaf-700 px-4 py-2 text-sm font-semibold text-white hover:bg-leaf-800"
        >
          Agregar
        </button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((v) => (
            <span
              key={String(v)}
              className="inline-flex items-center gap-1 rounded-full border border-leaf-200 bg-leaf-50 px-3 py-1 text-xs text-leaf-800"
            >
              {formatter(v)}
              <button
                type="button"
                onClick={() => onRemove(v)}
                className="ml-1 grid h-4 w-4 place-items-center rounded-full bg-leaf-200 text-[10px] text-leaf-900 hover:bg-leaf-300"
                aria-label="Quitar"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
